import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Grid,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import GoogleIcon from '../../assets/googleIcon.png';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';

import PageState from '../layouts/PageState';
import { useV2Session } from '../state/SessionProvider';
import { useV2Common } from '../state/CommonProvider';
import { getGenericLanding, getCustomerLanding } from '../services/store';
import Location from './Location';

// ---------------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------------

function BannerCarousel({ mobileBanners = [], desktopBanners = [] }) {
  const navigate = useNavigate();
  const isMobile = window.innerWidth < 768;
  const banners = isMobile
    ? (mobileBanners.length ? mobileBanners : desktopBanners)
    : (desktopBanners.length ? desktopBanners : mobileBanners);

  if (!banners.length) return null;

  const handleClick = (route) => {
    if (route) navigate(route);
  };

  return (
    <Box sx={{ width: '100%', mb: 3, overflow: 'hidden' }}>
      <Swiper
        modules={[Autoplay, Pagination]}
        autoplay={{ delay: 3500, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        loop
        style={{ borderRadius: 12, overflow: 'hidden' }}
      >
        {banners.map((b, i) => (
          <SwiperSlide key={i}>
            <Box
              component="img"
              src={b.url}
              alt={b.title || 'banner'}
              onClick={() => handleClick(b.route)}
              sx={{
                width: '100%',
                height: { xs: 160, sm: 220 },
                objectFit: 'cover',
                borderRadius: 2,
                cursor: b.route ? 'pointer' : 'default',
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

function CategoryGrid({ categories, onSelect }) {
  if (!categories?.length) return null;
  return (
    <Box mb={3}>
      <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
        Shop by category
      </Typography>
      <Grid container spacing={1.5}>
        {categories.map((cat) => (
          <Grid item xs={4} sm={3} key={cat.documentId || cat.id}>
            <Box
              onClick={() => onSelect(cat)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.75,
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Avatar
                src={cat.image}
                variant="rounded"
                sx={{ width: 52, height: 52, bgcolor: 'grey.100' }}
              >
                {cat.name?.[0]}
              </Avatar>
              <Typography variant="caption" align="center" fontWeight={500} noWrap sx={{ maxWidth: '100%' }}>
                {cat.name}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Latest orders teaser (logged-in only)
// ---------------------------------------------------------------------------

const STATUS_LABEL = {
  pending: 'Order placed',
  confirmed: 'Confirmed',
  processing: 'Being prepared',
  out_for_delivery: 'Out for delivery',
};

const STATUS_COLOR = {
  pending: 'default',
  confirmed: 'primary',
  processing: 'warning',
  out_for_delivery: 'success',
};

const LATEST_ORDERS_PREVIEW = 1;

function formatLatestOrderTotal(order) {
  const n = Number(order?.totalAmount ?? order?.total ?? 0);
  if (!Number.isFinite(n)) return '—';
  return `₹${Math.round(n)}`;
}

function latestOrderProductLine(order) {
  const raw = order?.productLines;
  const lines = Array.isArray(raw)
    ? raw
    : Array.isArray(order?.products)
      ? order.products.map((p) => ({
          name: String(p?.name || p?.productName || '').trim() || 'Item',
          quantity: Math.max(1, Math.floor(Number(p?.quantity) || 1)),
        }))
      : [];
  if (!lines.length) return null;
  return lines
    .map(({ name, quantity }) => (quantity > 1 ? `${name} ×${quantity}` : name))
    .join(', ');
}

function LatestOrders({ orders }) {
  const navigate = useNavigate();

  if (!orders?.length) return null;

  const hasMore = orders.length > LATEST_ORDERS_PREVIEW;
  const visibleOrders = orders.slice(0, LATEST_ORDERS_PREVIEW);

  return (
    <Box mb={3}>
      <Typography variant="subtitle1" fontWeight={600} mb={1}>
        Latest order
      </Typography>
      <Stack spacing={1}>
        {visibleOrders.map((o) => {
          const fullOrderId = String(o.orderId || o.id || '').trim();
          const shortId =
            fullOrderId.length > 6 ? fullOrderId.slice(-6).toUpperCase() : fullOrderId.toUpperCase();
          const productLine = latestOrderProductLine(o);
          return (
            <Box
              key={fullOrderId || o.orderId || o.id}
              onClick={() => {
                if (fullOrderId) {
                  navigate(`/v2/account/orders/${encodeURIComponent(fullOrderId)}`);
                } else {
                  navigate('/v2/account');
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500}>
                  Order #{shortId}
                </Typography>
                {productLine ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, lineHeight: 1.35 }}
                  >
                    {productLine}
                  </Typography>
                ) : null}
                <Typography variant="body2" fontWeight={700} sx={{ mt: 0.75 }}>
                  Total {formatLatestOrderTotal(o)}
                </Typography>
              </Box>
              <Chip
                label={STATUS_LABEL[o.order_status] || o.order_status}
                color={STATUS_COLOR[o.order_status] || 'default'}
                size="small"
                sx={{ flexShrink: 0 }}
              />
            </Box>
          );
        })}
      </Stack>
      {hasMore ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
          <Box
            component="button"
            type="button"
            onClick={() => navigate('/v2/account')}
            sx={{
              border: 'none',
              margin: 0,
              cursor: 'pointer',
              borderRadius: 2,
              px: 2.5,
              py: 1,
              typography: 'body2',
              fontWeight: 600,
              color: 'primary.main',
              fontFamily: 'inherit',
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            View all orders
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}


// ---------------------------------------------------------------------------
// Naatu fans testimonials
// ---------------------------------------------------------------------------

const TESTIMONIALS = [
  { title: 'Fresh and Delicious!', description: 'The country chicken was incredibly fresh, and the taste was unmatched.', customerName: 'Rahul', location: 'Gachibowli', rating: 5 },
  { title: 'Tender Mutton', description: 'The mutton was tender and cooked perfectly. Highly recommend.', customerName: 'Sneha', location: 'Kondapur', rating: 4.5 },
  { title: 'Farm Fresh Eggs!', description: 'The eggs were fresh and organic, just like they promised.', customerName: 'Arjun', location: 'Madhapur', rating: 5 },
  { title: 'Amazing Quality!', description: 'The country chicken was so tender and juicy. Perfect for a family meal.', customerName: 'Meena', location: 'Banjara Hills', rating: 4.8 },
  { title: 'Excellent Service!', description: 'The mutton was delivered on time and was super fresh. Great experience!', customerName: 'Vikram', location: 'Jubilee Hills', rating: 5 },
];

function NatuFansSays() {
  return (
    <Box mb={3}>
      <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
        What our Naatu fans say
      </Typography>
      <Box sx={{ overflow: 'hidden', mx: -2, px: 2 }}>
      <Swiper spaceBetween={10} slidesPerView="auto">
        {TESTIMONIALS.map((t, i) => (
          <SwiperSlide key={i} style={{ width: 200, maxWidth: 200 }}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                height: 160,
                overflow: 'hidden',
                bgcolor: '#A4243D',
                color: '#fff',
              }}
            >
              {/* Card body */}
              <Box sx={{ flex: 1, overflow: 'hidden', p: 1.25 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom noWrap>
                  {t.title}
                </Typography>
                <Typography variant="caption" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>
                  {t.description}
                </Typography>
              </Box>

              {/* Card footer */}
              <Box sx={{ bgcolor: '#FFF0D9', px: 1, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: '#A4243D', color: '#fff' }}>
                    {t.customerName[0]}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#A4243D', lineHeight: 1.2 }}>
                      {t.customerName}
                    </Typography>
                    <Typography sx={{ fontSize: 8, color: '#A4243D', lineHeight: 1.2 }}>
                      {t.location}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="img" src={GoogleIcon} alt="Google" sx={{ width: 14, height: 14 }} />
                    <Typography sx={{ fontSize: 8, color: '#000' }}>Google Rating</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <Typography sx={{ fontSize: 8, fontWeight: 700, color: '#000' }}>{t.rating}</Typography>
                    <Rating value={t.rating} precision={0.5} readOnly sx={{ fontSize: 10 }} />
                  </Box>
                </Box>
              </Box>
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Landing page
// ---------------------------------------------------------------------------

export default function Landing() {
  const { loggedIn, mobile } = useV2Session();
  const { showError } = useV2Common();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = loggedIn && mobile
        ? await getCustomerLanding({ mobile })
        : await getGenericLanding();
      setData(result.data);
    } catch (err) {
      setError(err);
      if (err.recoverable === false) showError({ message: err.message });
    } finally {
      setLoading(false);
    }
  }, [loggedIn, mobile, showError]);

  useEffect(() => { load(); }, [load]);

  return (
    <PageState loading={loading} error={error} onRetry={load} loadingMessage="Loading…">
      <Box px={2} pt={1} pb={10}>
        {/* Greeting */}
        {loggedIn && data?.customer && (
          <Typography variant="h6" fontWeight={700} mb={2}>
            Hey {data.customer.f_name || data.customer.name || 'there'} 👋
          </Typography>
        )}

        {/* Location → nearest store */}
        <Location savedAddressesCount={data?.addresses?.length ?? 0} />

        {/* Banners */}
        <BannerCarousel mobileBanners={data?.mobileBanners} desktopBanners={data?.desktopBanners} />

        {/* Latest orders — logged-in only */}
        {loggedIn && <LatestOrders orders={data?.currentOrders} />}

        {/* Categories */}
        <CategoryGrid
          categories={data?.categories}
          onSelect={(cat) => navigate(`/v2/catalog?category=${encodeURIComponent(cat.name)}`)}
        />

        {/* Testimonials */}
        <NatuFansSays />
      </Box>
    </PageState>
  );
}
